// Whether the person is writing in a text field (the keyboard is up), for the
// Dock to step out of the way or to widen its post box.
//
// Promise:
// - It turns on when such a field gets the focus and off when the focus
//   leaves it, or when the keyboard, seen open, closes while the field keeps
//   the focus (iOS's "done" can keep it: the Dock did not come back,
//   2026-09-26).
// - The keyboard is open when the visible part of the screen is shorter than
//   the tallest it has been (for this width) by more than KEYBOARD_MIN_PX.
import { useEffect, useRef, useState } from "react";

/** More than the accessory bar: the visible part shrank by this much, the keyboard is up. */
export const KEYBOARD_MIN_PX = 120;

/** A field the keyboard writes in (not buttons, checkboxes, or a document being read). */
export function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
  if (el.tagName !== "INPUT") return false;
  return ![
    "button",
    "checkbox",
    "radio",
    "submit",
    "reset",
    "file",
    "range",
    "color",
  ].includes((el as HTMLInputElement).type);
}

/** Whether a text field `where` accepts has the keyboard. */
export function useTypingIn(
  where: (active: Element) => boolean,
  enabled: boolean,
): boolean {
  const [typing, setTyping] = useState(false);
  const whereRef = useRef(where);
  whereRef.current = where;
  // frontend-development: not a fetch; follows the focus and the keyboard.
  useEffect(() => {
    if (!enabled) return;
    const focused = () => {
      const active = document.activeElement;
      return active !== null && isTextEntry(active) && whereRef.current(active);
    };
    const vv = window.visualViewport;
    let tallest = vv ? vv.height * vv.scale : 0;
    let width = vv?.width ?? 0;
    const keyboardOpen = () => {
      if (!vv) return true;
      const height = vv.height * vv.scale;
      // Turning the phone starts over.
      if (vv.width !== width) {
        width = vv.width;
        tallest = height;
      }
      tallest = Math.max(tallest, height);
      return height < tallest - KEYBOARD_MIN_PX;
    };
    // Off only after the keyboard was seen open: while it rises the height
    // has not shrunk yet, which is not "closed".
    let seenOpen = false;
    const onFocusIn = () => {
      seenOpen = false;
      setTyping(focused());
    };
    // At focusout the focus has not moved yet; focusin sees where it went.
    const onFocusOut = () => setTyping(false);
    const onResize = () => {
      if (!focused()) return;
      if (keyboardOpen()) {
        seenOpen = true;
        setTyping(true);
      } else if (seenOpen) {
        seenOpen = false;
        setTyping(false);
      }
    };
    setTyping(focused());
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    vv?.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", onResize);
    };
  }, [enabled]);
  return enabled && typing;
}
